import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown, LogOut, LayoutDashboard, Ruler, Code2, Plus, Trash2, Save,
  Copy, Check, Users, Target, Activity, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "chart", label: "Size Chart", icon: Ruler },
  { key: "embed", label: "Embed", icon: Code2 },
];

const Stat = ({ icon: Icon, label, value, gold }) => (
  <div className="bg-[#0A0A0A] border border-[#333] p-6 hover:border-white/50 transition-colors duration-200">
    <div className="flex items-center justify-between mb-4">
      <span className="overline text-white/40">{label}</span>
      <Icon className={`w-4 h-4 ${gold ? "gold" : "text-white/40"}`} strokeWidth={1.5} />
    </div>
    <div className={`font-display text-4xl ${gold ? "gold" : "text-white"}`}>{value}</div>
  </div>
);

const chartTip = {
  contentStyle: { background: "#141414", border: "1px solid #333", borderRadius: 0, color: "#fff", fontSize: 12 },
  labelStyle: { color: "#fff" },
  cursor: { fill: "rgba(255,255,255,0.05)" },
};

function Overview({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-8 animate-fade-up">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#333] border border-[#333]">
        <Stat icon={Activity} label="Total Suggestions" value={data.total} />
        <Stat icon={Target} label="Avg Confidence" value={`${data.avg_confidence}%`} gold />
        <Stat icon={Users} label="Genders Tracked" value={data.gender_distribution.length} />
        <Stat icon={TrendingUp} label="Sizes Offered" value={data.size_distribution.length} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-[#333] p-6">
          <span className="overline text-white/40">Size Distribution</span>
          <h3 className="font-display text-2xl uppercase mb-4 mt-1">Recommended Sizes</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.size_distribution}>
              <XAxis dataKey="size" stroke="#666" fontSize={12} tickLine={false} axisLine={{ stroke: "#333" }} />
              <YAxis stroke="#666" fontSize={12} tickLine={false} axisLine={{ stroke: "#333" }} allowDecimals={false} />
              <Tooltip {...chartTip} />
              <Bar dataKey="count">
                {data.size_distribution.map((e, i) => (
                  <Cell key={i} fill={i === data.size_distribution.reduce((m, c, j, a) => (c.count > a[m].count ? j : m), 0) ? "#D4AF37" : "#fff"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#0A0A0A] border border-[#333] p-6">
          <span className="overline text-white/40">Last 14 Days</span>
          <h3 className="font-display text-2xl uppercase mb-4 mt-1">Usage Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
              <XAxis dataKey="date" stroke="#666" fontSize={11} tickLine={false} axisLine={{ stroke: "#333" }} />
              <YAxis stroke="#666" fontSize={12} tickLine={false} axisLine={{ stroke: "#333" }} allowDecimals={false} />
              <Tooltip {...chartTip} />
              <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-[#333]">
        <div className="p-6 border-b border-[#333]">
          <span className="overline text-white/40">Activity Log</span>
          <h3 className="font-display text-2xl uppercase mt-1">Recent Suggestions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-table">
            <thead>
              <tr className="text-white/40 overline border-b border-[#333]">
                {["Size", "Gender", "Fit", "Height", "Weight", "Chest~", "Conf.", "When"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-white/30">No suggestions yet.</td></tr>
              )}
              {data.recent.map((r) => (
                <tr key={r.id} className="border-b border-[#1f1f1f] hover:bg-[#141414] transition-colors duration-150">
                  <td className="px-4 py-3 font-display text-lg gold">{r.recommended_size}</td>
                  <td className="px-4 py-3 capitalize text-white/70">{r.gender}</td>
                  <td className="px-4 py-3 capitalize text-white/70">{r.fit}</td>
                  <td className="px-4 py-3 text-white/70">{Math.round(r.height_cm)} cm</td>
                  <td className="px-4 py-3 text-white/70">{Math.round(r.weight_kg)} kg</td>
                  <td className="px-4 py-3 text-white/70">{r.estimated_chest}"</td>
                  <td className="px-4 py-3 text-white/70">{r.confidence}%</td>
                  <td className="px-4 py-3 text-white/40">{r.created_at.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartEditor({ chart, setChart }) {
  const [saving, setSaving] = useState(false);

  const update = (i, field, val) => {
    const sizes = [...chart.sizes];
    sizes[i] = { ...sizes[i], [field]: field === "size" ? val : val };
    setChart({ ...chart, sizes });
  };
  const addRow = () => setChart({ ...chart, sizes: [...chart.sizes, { size: "NEW", chest: 0, length: 0 }] });
  const removeRow = (i) => setChart({ ...chart, sizes: chart.sizes.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        fit_label: chart.fit_label,
        unit: chart.unit,
        brand_name: chart.brand_name,
        sizes: chart.sizes.map((s) => ({ size: s.size, chest: parseFloat(s.chest) || 0, length: parseFloat(s.length) || 0 })),
      };
      await api.put("/size-chart", payload);
      toast.success("Size chart saved");
    } catch {
      toast.error("Failed to save chart");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-[#141414] border border-[#333] px-3 py-2 text-white focus:outline-none focus:border-white transition-colors duration-200 w-full";

  return (
    <div className="animate-fade-up max-w-3xl">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="overline text-white/40 block mb-2">Brand Name</label>
          <input data-testid="chart-brand-input" className={inputCls} value={chart.brand_name} onChange={(e) => setChart({ ...chart, brand_name: e.target.value })} />
        </div>
        <div>
          <label className="overline text-white/40 block mb-2">Fit Label</label>
          <input data-testid="chart-fit-input" className={inputCls} value={chart.fit_label} onChange={(e) => setChart({ ...chart, fit_label: e.target.value })} />
        </div>
        <div>
          <label className="overline text-white/40 block mb-2">Unit</label>
          <select data-testid="chart-unit-select" className={inputCls} value={chart.unit} onChange={(e) => setChart({ ...chart, unit: e.target.value })}>
            <option value="in">inches</option>
            <option value="cm">cm</option>
          </select>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-[#333]">
        <div className="grid grid-cols-[1fr_1fr_1fr_44px] gap-px bg-[#333] border-b border-[#333] overline text-white/40">
          {["Size", `Chest (${chart.unit})`, `Length (${chart.unit})`, ""].map((h, i) => (
            <div key={i} className="bg-[#0A0A0A] px-4 py-3">{h}</div>
          ))}
        </div>
        {chart.sizes.map((s, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_44px] gap-px bg-[#1f1f1f] border-b border-[#1f1f1f]">
            <input data-testid={`row-size-${i}`} className="bg-[#0A0A0A] px-4 py-3 text-white font-bold focus:outline-none focus:bg-[#141414]" value={s.size} onChange={(e) => update(i, "size", e.target.value)} />
            <input data-testid={`row-chest-${i}`} type="number" className="bg-[#0A0A0A] px-4 py-3 text-white focus:outline-none focus:bg-[#141414]" value={s.chest} onChange={(e) => update(i, "chest", e.target.value)} />
            <input data-testid={`row-length-${i}`} type="number" className="bg-[#0A0A0A] px-4 py-3 text-white focus:outline-none focus:bg-[#141414]" value={s.length} onChange={(e) => update(i, "length", e.target.value)} />
            <button data-testid={`row-delete-${i}`} onClick={() => removeRow(i)} className="bg-[#0A0A0A] flex items-center justify-center text-white/40 hover:text-red-400 transition-colors duration-200">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-5">
        <button data-testid="add-row-button" onClick={addRow} className="border border-[#333] text-white font-bold uppercase tracking-wider px-5 py-3 flex items-center gap-2 hover:border-white transition-colors duration-200">
          <Plus className="w-4 h-4" /> Add Size
        </button>
        <button data-testid="save-chart-button" onClick={save} disabled={saving} className="bg-white text-black font-bold uppercase tracking-wider px-6 py-3 flex items-center gap-2 hover:bg-gold transition-colors duration-200 active:scale-[0.99] disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Chart"}
        </button>
      </div>
    </div>
  );
}

function Embed() {
  const [copied, setCopied] = useState("");
  const origin = window.location.origin;
  const scriptSnippet = `<script src="${API}/embed.js" data-button-text="Find My Size" defer></script>`;
  const iframeSnippet = `<iframe src="${origin}/widget" style="width:100%;max-width:440px;height:760px;border:none;" title="Find My Size"></iframe>`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1800);
  };

  const Block = ({ title, desc, code, k, testid }) => (
    <div className="bg-[#0A0A0A] border border-[#333] p-6">
      <span className="overline text-white/40">{title}</span>
      <p className="text-white/50 text-sm mt-1 mb-4">{desc}</p>
      <div className="bg-[#141414] border border-[#333] p-4 flex items-start justify-between gap-4">
        <code className="text-xs text-gold break-all leading-relaxed font-mono">{code}</code>
        <button data-testid={testid} onClick={() => copy(code, k)} className="shrink-0 border border-[#333] p-2 hover:border-white transition-colors duration-200">
          {copied === k ? <Check className="w-4 h-4 gold" /> : <Copy className="w-4 h-4 text-white/60" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-up max-w-3xl space-y-6">
      <div className="bg-[#141414] border border-[#333] p-6">
        <h3 className="font-display text-2xl uppercase mb-2">Add to your Shopify store</h3>
        <ol className="text-white/60 text-sm space-y-2 list-decimal pl-4">
          <li>In Shopify admin go to <b className="text-white">Online Store → Themes → Edit code</b>.</li>
          <li>Open your product template (e.g. <b className="text-white">product.liquid</b> or a section file).</li>
          <li>Paste the snippet below where you want the <b className="text-white">Find My Size</b> button to appear.</li>
          <li>Save. A button appears and opens the size finder in a popup.</li>
        </ol>
      </div>
      <Block
        title="Recommended · Button + Popup"
        desc="Drops a 'Find My Size' button that opens the finder in a modal. Best for product pages."
        code={scriptSnippet}
        k="script"
        testid="copy-script-button"
      />
      <Block
        title="Alternative · Inline iframe"
        desc="Embeds the finder directly inline on the page."
        code={iframeSnippet}
        k="iframe"
        testid="copy-iframe-button"
      />
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [chart, setChart] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/size-chart").then((r) => setChart(r.data)).catch(() => {});
  }, []);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#333] sticky top-0 z-40 backdrop-blur-xl bg-black/80">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 gold" strokeWidth={1.5} />
            <span className="font-display text-xl uppercase tracking-tight">FitFinder Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/40 text-xs hidden sm:block">{user?.email}</span>
            <button data-testid="logout-button" onClick={doLogout} className="border border-[#333] px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 hover:border-white transition-colors duration-200">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-px bg-[#333] border border-[#333] w-fit mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 transition-colors duration-200 ${
                tab === t.key ? "bg-white text-black" : "bg-[#0A0A0A] text-white/60 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview data={analytics} />}
        {tab === "chart" && chart && <ChartEditor chart={chart} setChart={setChart} />}
        {tab === "embed" && <Embed />}
      </div>
    </div>
  );
}
